import React, { useEffect, useState } from 'react';
import { Navigation, Wallet, Car, Star } from 'lucide-react';
import { HERO_STATS, DRIVERS } from '../mock';

export default function Hero({ onBookRide, onDriveWithUs }) {
  const [driverIdx, setDriverIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setDriverIdx((i) => (i + 1) % DRIVERS.length), 3500);
    return () => clearInterval(t);
  }, []);

  const driver = DRIVERS[driverIdx];
  const fare = [186, 142, 219, 98][driverIdx % 4];

  return (
    <section className="relative pt-16 md:pt-[72px] overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0 bg-[#0a0a0c]">
        <img
          src="https://images.unsplash.com/photo-1573108724029-4c46571d6490?w=2400&q=80&auto=format&fit=crop"
          alt=""
          loading="eager"
          className="w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0c]/90 via-[#0a0a0c]/60 to-[#0a0a0c]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0c]/70" />
      </div>

      <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-28 md:pb-36 grid lg:grid-cols-2 gap-12 lg:gap-6 items-center min-h-[82vh]">
        {/* Left: Copy */}
        <div className="text-white">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#FFB37A]/15 border border-[#FF6A1A]/40 px-3 py-1.5 text-[11px] font-semibold tracking-[0.14em] text-[#FFB37A]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6A1A] animate-pulse" />
            NOW LIVE · 12 INDIAN CITIES
          </span>

          <h1 className="mt-5 text-[44px] leading-[1.02] md:text-[68px] md:leading-[1.02] font-semibold tracking-[-0.02em]">
            India’s friendliest
            <br />
            <span className="text-[#FF6A1A]">ride</span>, on the way.
          </h1>

          <p className="mt-5 max-w-[460px] text-[15px] md:text-[17px] leading-relaxed text-white/75">
            Tap, ride, and pay in seconds. Real drivers, real maps, real fares.
            From Bangalore to every metro in India.
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              onClick={onBookRide}
              className="inline-flex items-center justify-center gap-2 bg-[#FF6A1A] hover:bg-[#ff5a05] text-white font-semibold rounded-full px-6 py-3.5 text-[15px] transition-transform hover:scale-[1.02] shadow-[0_18px_40px_-15px_rgba(255,106,26,0.7)]"
            >
              <Navigation size={18} className="-rotate-12" />
              Book a ride now
            </button>
            <button
              onClick={onDriveWithUs}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/25 backdrop-blur text-white font-semibold rounded-full px-6 py-3.5 text-[15px] transition-colors"
            >
              <Wallet size={18} />
              Earn as a driver
            </button>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-4 max-w-[480px]">
            {HERO_STATS.map((s) => (
              <div key={s.label}>
                <div className="text-[28px] md:text-[34px] font-semibold tracking-tight">{s.value}</div>
                <div className="text-[10.5px] md:text-[11px] font-semibold tracking-[0.14em] text-white/55">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Phone mockup */}
        <div className="relative flex justify-center lg:justify-end">
          <div className="relative w-[300px] md:w-[340px] aspect-[9/19.5]">
            <div className="absolute inset-0 rounded-[44px] bg-[#0f0f10] shadow-[0_40px_80px_-25px_rgba(0,0,0,0.7)] ring-1 ring-white/10">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#0f0f10] rounded-b-2xl" />
              {/* Screen */}
              <div className="absolute inset-[10px] rounded-[36px] overflow-hidden bg-[#e7eae3]">
                {/* fake map */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      'radial-gradient(circle at 30% 25%, #f3ede1 0%, #e7eae3 40%, #dde1d9 100%)',
                  }}
                />
                {/* roads */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 600" fill="none" preserveAspectRatio="xMidYMid slice">
                  <path d="M -10 90 C 60 110, 180 60, 320 120" stroke="#FF6A1A" strokeWidth="7" strokeLinecap="round" />
                  <path d="M -10 320 C 80 340, 200 280, 320 360" stroke="#FF6A1A" strokeWidth="7" strokeLinecap="round" />
                  <path d="M 60 0 C 80 120, 120 200, 90 360" stroke="#cfd4ca" strokeWidth="3" />
                  <path d="M 220 60 C 200 180, 250 300, 240 480" stroke="#cfd4ca" strokeWidth="3" />
                </svg>
                {/* pickup dot */}
                <div className="absolute top-[12%] left-[18%] w-4 h-4 rounded-full bg-white ring-4 ring-[#FF6A1A]" />
                {/* destination dot */}
                <div className="absolute top-[60%] right-[14%] w-4 h-4 rounded-full bg-[#0f0f10] ring-4 ring-white" />

                {/* ETA pill */}
                <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-[#0f0f10] text-white px-2.5 py-1.5 rounded-full text-[11px] font-semibold">
                  <Car size={12} />
                  2 min
                </div>
              </div>
            </div>

            {/* Driver card floating below */}
            <div className="absolute -bottom-12 -left-6 right-2 md:-left-10 md:right-4 bg-white rounded-2xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.45)] p-3.5 flex items-center gap-3 ring-1 ring-black/5">
              <div className="w-11 h-11 rounded-full bg-[#FFE5D2] text-[#FF6A1A] grid place-items-center font-bold text-[15px] flex-shrink-0">
                {driver.name.charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13.5px] font-semibold text-[#0f0f10] truncate">{driver.name} · {driver.car}</div>
                <div className="text-[11.5px] text-[#6b6b70] flex items-center gap-1.5 truncate">
                  <Star size={11} className="fill-[#FFB400] stroke-[#FFB400]" />
                  {driver.rating} · {driver.plate}
                </div>
              </div>
              <div className="bg-[#EAF8EE] text-[#0E8A3F] text-[11.5px] font-semibold px-2 py-1 rounded-md">₹{fare}</div>
            </div>
            {/* On the way button below card */}
            <div className="absolute -bottom-[112px] left-2 right-2 md:left-4 md:right-4">
              <div className="bg-[#0f0f10] text-white text-center text-[13.5px] font-semibold rounded-2xl py-3.5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)]">
                On the way
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
